import {Badge, Button, Card, Group, Loader, Modal, Stack, Table, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {IconPlus} from '@tabler/icons-react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from 'react'

import {inviteUser, listUsers} from 'src/api/admin/api'

export const Route = createFileRoute('/_authed/admin/users/')({
    component: AdminUsersPage,
})

function AdminUsersPage() {
    const queryClient = useQueryClient()
    const [inviteModalOpen, setInviteModalOpen] = useState(false)

    const {data: users = [], isLoading} = useQuery({
        queryKey: ['admin', 'users'],
        queryFn: () => listUsers(),
    })

    const inviteMutation = useMutation({
        mutationFn: (body: {email: string; name: string}) => inviteUser(body),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'users']})
            setInviteModalOpen(false)
            notifications.show({color: 'green', message: 'Invitation sent'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    return (
        <Card withBorder padding="md">
            <Card.Section withBorder inheritPadding py="md">
                <Group justify="space-between">
                    <span>Users</span>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={() => setInviteModalOpen(true)}
                    >
                        Invite Admin User
                    </Button>
                </Group>
            </Card.Section>
            <Card.Section inheritPadding pt="md">
                {isLoading ? (
                    <Loader size="sm" />
                ) : (
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>Email</Table.Th>
                                <Table.Th>Role</Table.Th>
                                <Table.Th>Status</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {users.map((row) => (
                                <Table.Tr key={row.id}>
                                    <Table.Td>
                                        <Link
                                            to="/admin/users/$userId"
                                            params={{userId: row.id}}
                                            style={{
                                                color: 'inherit',
                                                textDecoration: 'none',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {row.name}
                                        </Link>
                                    </Table.Td>
                                    <Table.Td>{row.email}</Table.Td>
                                    <Table.Td>
                                        <Badge color={row.isAdmin ? 'blue' : 'gray'}>
                                            {row.isAdmin ? 'Admin' : 'Client'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge color={row.deactivatedAt ? 'red' : 'green'}>
                                            {row.deactivatedAt ? 'Deactivated' : 'Active'}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </Card.Section>

            <Modal
                title="Invite Admin User"
                opened={inviteModalOpen}
                onClose={() => setInviteModalOpen(false)}
            >
                <InviteAdminForm
                    onSubmit={(values) => inviteMutation.mutate(values)}
                    onCancel={() => setInviteModalOpen(false)}
                    loading={inviteMutation.isPending}
                />
            </Modal>
        </Card>
    )
}

type InviteAdminFormProps = {
    onSubmit: (values: {email: string; name: string}) => void
    onCancel: () => void
    loading: boolean
}

function InviteAdminForm({onSubmit, onCancel, loading}: InviteAdminFormProps) {
    const form = useForm({
        initialValues: {email: '', name: ''},
        validate: {
            email: (value) =>
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '') ? null : 'Enter a valid email',
            name: (value) => (value?.trim() ? null : 'Enter a name'),
        },
    })

    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TextInput
                    label="Email"
                    placeholder="you@example.com"
                    {...form.getInputProps('email')}
                />
                <TextInput label="Name" placeholder="Full Name" {...form.getInputProps('name')} />
                <Group>
                    <Button variant="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={loading}>
                        Send Invitation
                    </Button>
                </Group>
            </Stack>
        </form>
    )
}
